import React, { useState, useContext } from 'react'
import { airconBookingPath } from '../../../utils/markets'
import { MdAir, MdHandyman } from 'react-icons/md'
import { GiHairStrands, GiMagicBroom, GiPartyPopper, GiFoodTruck, GiFamilyHouse, GiPaintRoller, GiReceiveMoney } from 'react-icons/gi'
import { IoBody } from 'react-icons/io5'
import { LoadingCover } from '../../commons/LoadingCover';
import Link from 'next/link'
import { useRouter } from 'next/router'
import { LocationContext } from '../../../context/LocationContext';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';

// Service icon mapping with colors
const serviceConfig = {
  "Aircon Services": {
    icon: MdAir,
    label: "Aircon",
    gradient: "from-blue-500 to-cyan-400",
    bgLight: "bg-blue-50",
    textColor: "text-blue-600"
  },
  "Handyman Services": {
    icon: MdHandyman,
    label: "Handyman",
    gradient: "from-amber-500 to-yellow-400",
    bgLight: "bg-amber-50",
    textColor: "text-amber-600"
  },
  "Domestic Services": {
    icon: GiMagicBroom,
    label: "Domestic",
    gradient: "from-purple-500 to-violet-400",
    bgLight: "bg-purple-50",
    textColor: "text-purple-600"
  },
  "Beauty Services": {
    icon: GiHairStrands,
    label: "Beauty",
    gradient: "from-pink-500 to-rose-400",
    bgLight: "bg-pink-50",
    textColor: "text-pink-600"
  },
  "Fitness Trainer": {
    icon: IoBody,
    label: "Fitness",
    gradient: "from-green-500 to-emerald-400",
    bgLight: "bg-green-50",
    textColor: "text-green-600"
  },
  "Housing": {
    icon: GiFamilyHouse,
    label: "Housing",
    gradient: "from-indigo-500 to-blue-400",
    bgLight: "bg-indigo-50",
    textColor: "text-indigo-600"
  },
  "Financial": {
    icon: GiReceiveMoney,
    label: "Financial",
    gradient: "from-emerald-500 to-teal-400",
    bgLight: "bg-emerald-50",
    textColor: "text-emerald-600"
  },
  "Food Catering": {
    icon: GiFoodTruck,
    label: "Catering",
    gradient: "from-orange-500 to-amber-400",
    bgLight: "bg-orange-50",
    textColor: "text-orange-600"
  },
  "Event Planning": {
    icon: GiPartyPopper,
    label: "Event",
    gradient: "from-fuchsia-500 to-pink-400",
    bgLight: "bg-fuchsia-50",
    textColor: "text-fuchsia-600"
  },
  "Interior Designing": {
    icon: GiPaintRoller,
    label: "Interior",
    gradient: "from-slate-500 to-gray-400",
    bgLight: "bg-slate-50",
    textColor: "text-slate-600"
  }
};

// Animation variants - simplified to prevent layout shifts
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  }
};

const searchVariants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

// Service Card Component
const ServiceCard = ({ requestCategorySetting, href, isDisabled }) => {
  const config = serviceConfig[requestCategorySetting.requestType] || {
    icon: MdAir,
    label: requestCategorySetting.requestType,
    gradient: "from-gray-500 to-gray-400",
    bgLight: "bg-gray-50",
    textColor: "text-gray-600"
  };

  const IconComponent = config.icon;

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ 
        scale: 1.05, 
        y: -5,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
    >
      <Link 
        href={href}
        prefetch={false} 
        className={`
          block group relative
          ${isDisabled ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <div className={`
          relative overflow-hidden rounded-xl p-3
          ${config.bgLight}
          border border-transparent
          transition-all duration-200 ease-out
          hover:border-orange-200 hover:shadow-md
        `}>
          {/* Icon Container */}
          <div className="relative flex flex-col items-center gap-1.5">
            <div className={`
              relative flex items-center justify-center
              w-10 h-10 rounded-lg
              bg-linear-to-br ${config.gradient}
              shadow
              transition-transform duration-200
              group-hover:scale-105
            `}>
              <IconComponent className="text-white text-xl" />
            </div>
            
            {/* Label */}
            <h4 className={`
              font-semibold text-xs
              ${config.textColor}
              transition-colors duration-200
              group-hover:text-orange-600
            `}>
              {config.label}
            </h4>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const RequestList = (props) => {
  const router = useRouter()
  
  const routeCountryCd = router.query.countryCd
  const routeLocality = router.query.locality

  const { countryCd, locality } = useContext(LocationContext)

  const [ requestCategorySettings, setRequestCategorySettings ] = useState(props.requestCategorySettings);
  const [ isLoadingCover, setIsLoadingCover ] = useState(false)
  const [ searchText, setSearchText ] = useState("")
  const [ isFocused, setIsFocused ] = useState(false)

  const handleSearch = (e) => {
    e.preventDefault()
    setSearchText(e.target.value) 
    if(e.target.value.length > 2){
      setRequestCategorySettings(props.requestCategorySettings.filter((list) => JSON.stringify(list).toLowerCase().indexOf(e.target.value.toLowerCase()) != -1))
    } else {
      setRequestCategorySettings(props.requestCategorySettings)
    }    
  }

  const getHref = (requestCategorySetting) => {
    // Aircon has no question wizard - its page opens the item picker.
    if (requestCategorySetting.requestType === "Aircon Services") {
      return airconBookingPath(routeCountryCd || countryCd);
    }
    if (routeCountryCd && routeLocality) {
      return `/services/${routeCountryCd}?requestType=${encodeURIComponent(requestCategorySetting.requestType)}`;
    }
    return `/services/${countryCd}?requestType=${encodeURIComponent(requestCategorySetting.requestType)}`;
  };

  return (
    <div className="pb-2">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-3">
          <div className="text-lg sm:text-xl font-bold text-gray-800">
            What service do you need?
          </div>
          <p className="text-xs text-gray-500">Select a service to get started</p>
        </div>

        {/* Search Bar */}
        <motion.div 
          className="px-3 mb-4"
          variants={searchVariants}
          initial="initial"
          animate="animate"
        >
          <div className={`
            relative max-w-md mx-auto
            transition-all duration-300
            ${isFocused ? 'transform scale-[1.02]' : ''}
          `}>
            <div className={`
              absolute inset-y-0 left-0 flex items-center pl-4
              transition-colors duration-300
              ${isFocused ? 'text-orange-500' : 'text-gray-400'}
            `}>
              <Search className="w-5 h-5" />
            </div>
            <input
              type="search"
              className={`
                w-full pl-12 pr-4 py-3.5
                text-gray-700 placeholder-gray-400
                bg-gray-50 rounded-2xl
                border-2 transition-all duration-300
                ${isFocused 
                  ? 'border-orange-400 bg-white shadow-lg shadow-orange-100/50 ring-4 ring-orange-50' 
                  : 'border-transparent hover:border-gray-200 hover:bg-white'
                }
                focus:outline-none
              `}
              placeholder="Search services..." 
              name="searchText" 
              value={searchText} 
              onChange={(e) => handleSearch(e)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {searchText && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setSearchText('');
                  setRequestCategorySettings(props.requestCategorySettings);
                }}
              >
                <span className="text-xl">&times;</span>
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Services Grid - min-height prevents layout shift when filtering */}
        <div className="min-h-50">
          {requestCategorySettings && requestCategorySettings.length > 0 && (
            <motion.div 
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 px-2 sm:px-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {[...requestCategorySettings].map((requestCategorySetting, i) => (
                <ServiceCard
                  key={i}
                  requestCategorySetting={requestCategorySetting}
                  href={getHref(requestCategorySetting)}
                  isDisabled={!countryCd && !routeCountryCd}
                />
              ))}
            </motion.div>
          )}

          {/* Empty State */}
          {requestCategorySettings?.length === 0 && searchText && (
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-gray-400 text-6xl mb-4">🔍</div>
              <p className="text-gray-500">No services found for &quot;{searchText}&quot;</p>
              <button 
                className="mt-4 text-orange-500 hover:text-orange-600 font-medium"
                onClick={() => {
                  setSearchText('');
                  setRequestCategorySettings(props.requestCategorySettings);
                }}
              >
                Clear search
              </button>
            </motion.div>
          )}
        </div>
      </div>
      
      {isLoadingCover && <LoadingCover />}
    </div>
  )
}
